"use client";
import { useAuthStore } from "@/store/useAuthStore";
import axios from "axios";
import useSWR from "swr";

const API = process.env.NEXT_PUBLIC_API_BASE_URL;

// ── Types ─────────────────────────────────────────────────────────────────────

export type AgencyStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface OfficeLocation {
  city: string;
  country: string;
  address?: string;
}

export interface Agency {
  id: string;
  userId: string;
  name: string;
  logo: string | null;
  description: string;
  websiteUrl: string | null;
  contactEmail: string;
  phone: string | null;
  countriesCovered: string[];
  fieldsCovered: string[];
  servicesOffered: string[];
  officeLocations: OfficeLocation[];
  teamSize: number | null;
  yearFounded: number | null;
  licenseNumber: string | null;
  rating: number;
  totalReviews: number;
  agencyStatus: AgencyStatus;
  reviewedBy: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  consultationProfileId?: string | null;
  isAvailableForHire?: boolean;
}

export interface AgencyRegisterPayload {
  name: string;
  description: string;
  contactEmail: string;
  countriesCovered: string[];
  fieldsCovered: string[];
  servicesOffered: string[];
  logo?: string;
  websiteUrl?: string;
  phone?: string;
  officeLocations?: OfficeLocation[];
  teamSize?: number;
  yearFounded?: number;
  licenseNumber?: string;
}

// ── Fetchers ──────────────────────────────────────────────────────────────────

const publicFetcher = (url: string) => axios.get(url).then((r) => r.data);

const authFetcher = ([url, token]: [string, string]) =>
  axios
    .get(url, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.data);

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAgencies() {
  const { accessToken, id } = useAuthStore();

  const { data: agenciesData, isLoading: agenciesLoading } = useSWR(
    `${API}/agencies`,
    publicFetcher,
    { revalidateOnFocus: false, dedupingInterval: 30000 }
  );

  // GET /agencies/me — authoritative profile + status for the caller
  const {
    data: myAgencyData,
    error: myAgencyError,
    isLoading: myAgencyLoading,
    mutate: mutateMine,
  } = useSWR(
    id && accessToken ? [`${API}/agencies/me`, accessToken] : null,
    authFetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: 10000,
      // 404 means "no agency profile" — don't keep retrying
      shouldRetryOnError: false,
    }
  );

  const myAgency = (myAgencyData?.data ?? myAgencyData) as Agency | undefined;

  // Derive a slim status object so callers can branch easily.
  const applicationStatus = myAgency
    ? { agencyStatus: myAgency.agencyStatus }
    : undefined;

  const registerAgency = async (
    payload: AgencyRegisterPayload
  ): Promise<Agency> => {
    const res = await axios.post(`${API}/agencies/register`, payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const created = (res.data?.data ?? res.data) as Agency;
    mutateMine();
    return created;
  };

  const updateMyAgency = async (
    payload: Partial<Agency>
  ): Promise<Agency> => {
    const res = await axios.patch(`${API}/agencies/me`, payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const updated = (res.data?.data ?? res.data) as Agency;
    mutateMine();
    return updated;
  };

  type RawAgency = Agency & { is_available_for_hire?: boolean };
  const rawAgencies = (agenciesData?.data ?? agenciesData) as
    | RawAgency[]
    | undefined;
  const normalizedAgencies = rawAgencies?.map((a: RawAgency) => ({
    ...a,
    isAvailableForHire: a.isAvailableForHire ?? a.is_available_for_hire,
  })) as Agency[] | undefined;

  return {
    agencies: normalizedAgencies,
    loading: agenciesLoading,
    applicationStatus,
    myAgency,
    myAgencyError,
    myAgencyLoading,
    registerAgency,
    updateMyAgency,
    mutateStatus: mutateMine,
    mutateMine,
  };
}
